//  src/services/timeService.js
import axios from "axios";
import moment from "moment-timezone";

// ✅ BigDataCloud Time Zone by Location
// Put your key in .env as VITE_BDC_KEY (Vite) or REACT_APP_BDC_KEY (CRA)
// Fallback to the hardcoded key you provided (not recommended for production)
const TIME_API_URL = "https://api-bdc.net/data/timezone-by-location";
const API_KEY = "bdc_9fce5cec57aa4a98a2bd75de32ccab6d";

// ---- Simple localStorage cache (1 day) ----
const TTL_MS = 24 * 60 * 60 * 1000;
const round = (n) => Number.parseFloat(n).toFixed(4); // reduces cache keys explosion
const k = (lat, lng) => `bdc-tz:${round(lat)}:${round(lng)}`;

function readCache(lat, lng) {
  try {
    const raw = localStorage.getItem(k(lat, lng));
    if (!raw) return null;
    const obj = JSON.parse(raw);
    if (!obj || typeof obj !== "object") return null;
    if (Date.now() - obj.cachedAt > TTL_MS) return null;
    return obj.value;
  } catch {
    return null;
  }
}

function writeCache(lat, lng, value) {
  try {
    localStorage.setItem(
      k(lat, lng),
      JSON.stringify({ value, cachedAt: Date.now() })
    );
  } catch {}
}

/**
 * Fetch current local time & timezone for given coordinates using BigDataCloud.
 * @param {number} lat
 * @param {number} lon
 * @returns {{
 *   dateTime: string,         // ISO string from API, e.g. "2025-11-06T15:32:10+05:00"
 *   formatted: string,        // space-separated convenience, e.g. "2025-11-06 15:32:10+05:00"
 *   zoneName: string,         // IANA ID, e.g. "Asia/Karachi"
 *   abbreviation: string,     // e.g. "PKT"
 *   gmtOffset: number         // offset in seconds, e.g. 18000
 * }}
 */
export const fetchTimeFromCoords = async (lat, lon) => {
  // 1) cache first
  const cached = readCache(lat, lon);
  if (cached) return cached;

  try {
    const res = await axios.get(TIME_API_URL, {
      params: {
        // ✅ BigDataCloud expects "latitude" and "longitude"
        latitude: lat,
        longitude: lon,
        key: API_KEY,
      },
      timeout: 10000,
      // axios `validateStatus` is optional; default treats 2xx as ok
    });

    // 2) basic HTTP check
    if (res.status < 200 || res.status >= 300) {
      throw new Error(`HTTP ${res.status}: ${res.statusText || "Request failed"}`);
    }

    const data = res.data || {};
    // 3) sanity check: BigDataCloud returns localTime for "now" by default
    if (!data.localTime) {
      // Some errors arrive with message fields
      const details =
        data.message || data.error || "Missing localTime in response.";
      throw new Error(`BigDataCloud API error: ${details}`);
    }

    // 4) normalize return shape
    const iso = String(data.localTime); // ISO like "2025-11-06T15:32:10+05:00"
    const formatted = iso.replace("T", " "); // convenience for older code

    const value = {
      dateTime: iso,
      formatted,
      zoneName: data.ianaTimeId || data.timezone || "", // prefer IANA
      abbreviation:
        data.effectiveTimeZoneShort ||
        data.timeZoneAbbreviation ||
        "", // fallback if present
      gmtOffset: Number(data.utcOffsetSeconds ?? 0), // seconds
    };

    // 5) cache & return
    writeCache(lat, lon, value);
    return value;
  } catch (err) {
    console.error("❌ Failed to fetch time from coordinates (BigDataCloud)", err);
    // Optional: fallback shape so callers don't crash
    throw err;
  }
};

// ✅ Step 2: Determine current time in masjid timezone
export const getMasjidCurrentTime = (zoneName) => {
  try {
    const now = moment().tz(zoneName);
    return {
      time: now.format("h:mm A"),
      dateTime: now,
    };
  } catch (err) {
    console.error("❌ Invalid timezone, using local", zoneName, err);
    const now = moment();
    return {
      time: now.format("h:mm A"),
      dateTime: now,
    };
  }
};

// ✅ Step 3: Example usage inside your main component or function
// (you can adjust based on your actual component logic)
/*
async function setupMasjidTime(lat, long) {
  const td = await fetchTimeFromCoords(lat, long);
  const zone = td?.zoneName || "Asia/Karachi"; // fallback
  const currentMasjidTime = getMasjidCurrentTime(zone);

  console.log("🕌 Masjid Current Time:", currentMasjidTime.time);

  // ✅ When calculating "next iqamah", use `moment.tz(iqamahTime, "h:mm A", zone)`
  // Example:
  const nextIqamah = moment.tz("8:00 PM", "h:mm A", zone);
  const diffMinutes = nextIqamah.diff(currentMasjidTime.dateTime, "minutes");

  console.log(`Next Iqamah (Isha): ${diffMinutes} minutes from now`);
}
*/